import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import i18n from '@/libs/i18n'
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import useMongoDBFunctions from '@/views/habit/useMongoDBFunctions'
import XLSX from 'xlsx-js-style'
import JSZip from 'jszip'

export default function useConfirmationPlanningList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { handleError } = useCommon()
  const { updateItem } = useLambda()
  const { confirmationPlanningQueryWithOffset, supervisorsCountByLocation, workersCountByRole } = useMongoDBFunctions()
  const { client = null, role = null } = store.state?.userStore?.userData
  const confirmationPlannings = ref([])
  const collection = 'confirmation_planning'

  // Table Headers
  const tableColumns = computed(() => {
    return [
      { key: 'roleName.name', label: i18n.t('message.tableHeader.role'), sortable: true },
      { key: 'processName.name', label: i18n.t('message.tableHeader.process'), sortable: true },
      { key: 'confirmationsPerYear', label: i18n.t('message.tableHeader.confirmationsPerYear'), sortable: true },
      { key: 'physicalLocations', label: i18n.t('message.tableHeader.physical_locations'), sortable: true},
      { key: 'locations', label: i18n.t('message.tableHeader.locations'), sortable: true},
      { key: 'Actions', label: i18n.t('message.tableHeader.actions') },
    ]
  })
  const totalConfirmationPlannings = ref(0)
  const perPage = ref(10)
  const currentPage = ref(1)
  const perPageOptions = [5, 10, 15, 20, 25, 30, 40, 50, 100]
  const searchQuery = ref('')
  const defaultSortBy = 'roleName.name'
  const sortBy = ref(defaultSortBy)
  const isSortDirDesc = ref(false)
  const isLoading = ref(true)
  const processFilter = ref([]);
  const roleFilter = ref([]);
  const locationFilter = ref([])
  const physicalLocationFilter = ref([])
  const isExporting = ref(false)

  const fetchConfirmationPlannings = async () => {
    isLoading.value = true

    try {
      const input = {
        limit: perPage.value,
        total: currentPage.value === 1,
        offset: (currentPage.value * perPage.value) - perPage.value,
        search: searchQuery.value,
        sortBy: sortBy.value || defaultSortBy,
        sortOrder: isSortDirDesc.value ? -1 : 1,
        processes: processFilter.value,
        roles: roleFilter.value,
        client_id: (role !== 'admin') ? client.$oid : null,
        locationFilter: locationFilter.value,
        physicalLocationFilter: physicalLocationFilter.value,
      }
    
      const items = await confirmationPlanningQueryWithOffset(input)

      // Format physical locations
      items.confirmationPlannings?.forEach(cp => {
        const physicalLocations = new Set()
        cp.locationsData?.forEach(l => {
          if (l.physicalLocationName) physicalLocations.add(l.physicalLocationName)
        })
        cp.physicalLocations = Array.from(physicalLocations).join(', ')
        cp.locations = cp.locationsData?.map(l => l.location).join(', ') || ''
      })

      confirmationPlannings.value = items?.confirmationPlannings || []
      if (currentPage.value === 1) totalConfirmationPlannings.value = items?.totalRecords || 0
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: i18n.t('message.err_confirmation_planning') })
    } finally {
      isLoading.value = false
    }
  }

  const deleteConfirmationPlanning = async (id) => {
    try {
      const query = { _id: oid(id) }
      const action = { $set: { deleted: true } }

      await updateItem({ collection, query, action })

      fetchConfirmationPlannings()
      showSuccessMessage(i18n.t('message.confirmation_planning_remove'))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.confirmation_planning_remove_error'))
    }
  }

  const showingMessage = computed(() => {
    const from = ((currentPage.value * perPage.value) - perPage.value) + (confirmationPlannings.value.length ? 1 : 0)
    const to = confirmationPlannings.value.length + ((currentPage.value * perPage.value) - perPage.value)

    return i18n.tc('message.paginationText', 0, { from: from, to: to, total: totalConfirmationPlannings.value })
  })

  const updateFilter = (data) => {
    processFilter.value = data.processFilter
    roleFilter.value = data.roleFilter
    locationFilter.value = data.locationFilter
    physicalLocationFilter.value = data.physicalLocationFilter

    fetchConfirmationPlannings()
  }

  const exportToExcel = async () => {
    isExporting.value = true

    try {
      // Obtener todos los datos sin paginación
      const input = {
        limit: 10000,
        total: false,
        offset: 0,
        search: '',
        sortBy: 'processName.name',
        sortOrder: 1,
        processes: [],
        roles: [],
        client_id: (role !== 'admin') ? client.$oid : null,
        locationFilter: [],
        physicalLocationFilter: [],
      }

      const items = await confirmationPlanningQueryWithOffset(input)
      const allData = items?.confirmationPlannings || []

      if (!allData.length) {
        showErrorMessage(i18n.t('message.no_records'))
        return
      }

      // Obtener cantidad de supervisores por departamento y empleados por rol
      const clientId = (role !== 'admin') ? client.$oid : null
      const [{ supervisorsByLocation }, { workersByRole }] = await Promise.all([
        supervisorsCountByLocation({ client_id: clientId }),
        workersCountByRole({ client_id: clientId })
      ])
      const supervisorsByLocationMap = new Map(supervisorsByLocation.map(({ _id, count }) => [String(_id), count]))
      const employeesByRoleMap = new Map(workersByRole.map(({ location, role, count }) => [`${location}|${role}`, count]))

      // Crear estructura de matriz
      // 1. Recopilar todas las combinaciones únicas de physical_location > location > role
      const hierarchyMap = new Map() // key: physicalLocationId, value: Map(locationId -> Set(roleId))
      const physicalLocationNames = new Map()
      const locationNames = new Map()
      const roleNames = new Map()
      const processMap = new Map() // key: processId, value: process name
      const noLocationRoles = new Set() // Roles sin departamento asignado

      allData.forEach(item => {
        const processId = item.processName[0]?._id
        const processName = item.processName[0]?.name
        const roleId = item.roleName[0]?._id
        const roleName = item.roleName[0]?.name

        if (processId && !processMap.has(processId)) {
          processMap.set(processId, processName)
        }

        if (roleId && roleName) {
          roleNames.set(roleId, roleName)
        }

        // Verificar si tiene locations asignadas
        const hasLocations = item.locationsData?.length > 0

        if (hasLocations) {
          // Procesar cada location del item
          item.locationsData.forEach(loc => {
            const physicalLocationId = loc.physical_location || 'no_physical'
            const physicalLocationName = loc.physicalLocationName || 'Sin Localidad Física'
            const locationId = loc._id
            const locationName = loc.location

            physicalLocationNames.set(physicalLocationId, physicalLocationName)
            locationNames.set(locationId, locationName)

            if (!hierarchyMap.has(physicalLocationId)) {
              hierarchyMap.set(physicalLocationId, new Map())
            }

            const locationsInPhysical = hierarchyMap.get(physicalLocationId)
            if (!locationsInPhysical.has(locationId)) {
              locationsInPhysical.set(locationId, new Set())
            }

            if (roleId) {
              locationsInPhysical.get(locationId).add(roleId)
            }
          })
        } else {
          // Item sin location asignada
          if (roleId) {
            noLocationRoles.add(roleId)
          }
        }
      })

      // 2. Crear headers con jerarquía
      const processesLabel = i18n.t('label.Processes')
      const rolesLabel = i18n.t('label.Roles')
      const supervisorsLabel = i18n.t('supervisors_per_department')
      const confirmationsLabel = i18n.t('confirmations_per_year_per_department')
      const employeesLabel = i18n.t('employees_per_role')

      const headers = []
      const header1 = ['', ''] // Row 0: Physical Locations
      const header2 = ['', i18n.t('Department')] // Row 1: Locations (Departamentos)
      const headerSup = ['', supervisorsLabel] // Row 2: Supervisores por Departamento
      const headerSub = ['', confirmationsLabel] // Row 3: Confirmaciones al año por Departamento
      const header3 = ['', rolesLabel] // Row 4: Roles
      const headerEmp = ['', employeesLabel] // Row 5: Empleados por Rol

      const columnMapping = [] // Para mapear cada columna a su physical_location, location, role
      const departmentSpans = [] // Para mapear el ancho en columnas de cada departamento

      // Ordenar por nombres
      const sortedPhysicalLocations = Array.from(hierarchyMap.entries()).sort((a, b) => {
        const nameA = physicalLocationNames.get(a[0])
        const nameB = physicalLocationNames.get(b[0])
        return nameA.localeCompare(nameB)
      })

      sortedPhysicalLocations.forEach(([physicalLocationId, locationsMap]) => {
        const physicalLocationName = physicalLocationNames.get(physicalLocationId)
        const sortedLocations = Array.from(locationsMap.entries()).sort((a, b) => {
          const nameA = locationNames.get(a[0])
          const nameB = locationNames.get(b[0])
          return nameA.localeCompare(nameB)
        })

        let physicalLocationColSpan = 0

        sortedLocations.forEach(([locationId, rolesSet]) => {
          const locationName = locationNames.get(locationId)
          const sortedRoles = Array.from(rolesSet).sort((a, b) => {
            const nameA = roleNames.get(a)
            const nameB = roleNames.get(b)
            return nameA.localeCompare(nameB)
          })

          departmentSpans.push({ locationId, span: rolesSet.size })

          sortedRoles.forEach(roleId => {
            const roleName = roleNames.get(roleId)

            header2.push(locationName)
            headerSup.push('')
            headerSub.push('')
            header3.push(roleName)
            headerEmp.push('')
            physicalLocationColSpan++

            columnMapping.push({
              physicalLocationId,
              locationId,
              roleId
            })
          })
        })

        // Agregar physical location header con colspan
        for (let i = 0; i < physicalLocationColSpan; i++) {
          header1.push(i === 0 ? physicalLocationName : '')
        }
      })

      // Agregar columnas para roles sin localidad asignada
      const noLocationLabel = i18n.t('no_location_assigned')
      const noDepartmentLabel = i18n.t('no_department_assigned')

      if (noLocationRoles.size > 0) {
        const sortedNoLocationRoles = Array.from(noLocationRoles).sort((a, b) => {
          const nameA = roleNames.get(a)
          const nameB = roleNames.get(b)
          return nameA.localeCompare(nameB)
        })

        sortedNoLocationRoles.forEach(roleId => {
          const roleName = roleNames.get(roleId)

          header1.push(noLocationLabel)
          header2.push(noDepartmentLabel)
          headerSup.push('')
          headerSub.push('')
          header3.push(roleName)
          headerEmp.push('')

          columnMapping.push({
            physicalLocationId: 'no_location',
            locationId: 'no_location',
            roleId,
            isNoLocation: true
          })
        })

        // El grupo "Sin departamento asignado" abarca todas sus columnas de roles
        departmentSpans.push({ locationId: 'no_location', span: noLocationRoles.size })
      }

      // Fila 1 vacía: las localidades empiezan en la fila 2 del Excel
      const headerEmpty = header1.map(() => '')
      headers.push(headerEmpty, header1, header2, headerSup, headerSub, header3, headerEmp)

      // 3. Crear matriz de datos
      const dataRows = []
      const sortedProcesses = Array.from(processMap.entries()).sort((a, b) => a[1].localeCompare(b[1]))

      sortedProcesses.forEach(([processId, processName]) => {
        const row = [processesLabel, processName]

        columnMapping.forEach(({ physicalLocationId, locationId, roleId, isNoLocation }) => {
          let matchingItem

          if (isNoLocation) {
            // Buscar items sin localidad asignada
            matchingItem = allData.find(item => {
              const itemProcessId = item.processName[0]?._id
              const itemRoleId = item.roleName[0]?._id
              const hasNoLocation = !item.locationsData || item.locationsData.length === 0

              return itemProcessId === processId && itemRoleId === roleId && hasNoLocation
            })
          } else {
            // Buscar el item que coincide con process, location y role
            matchingItem = allData.find(item => {
              const itemProcessId = item.processName[0]?._id
              const itemRoleId = item.roleName[0]?._id
              const hasLocation = item.locationsData?.some(loc => {
                const locId = loc._id
                const locPhysicalId = loc.physical_location || 'no_physical'
                return locId === locationId && locPhysicalId === physicalLocationId
              })

              return itemProcessId === processId && itemRoleId === roleId && hasLocation
            })
          }

          row.push(matchingItem?.confirmationsPerYear || '')
        })

        dataRows.push(row)
      })

      // Calcular el total de confirmaciones por columna (rol)
      const columnTotals = columnMapping.map((col, colIndex) => {
        let total = 0
        dataRows.forEach(row => {
          const value = row[2 + colIndex] // +2 porque las primeras 2 columnas son etiquetas
          if (value && !isNaN(value)) {
            total += Number(value)
          }
        })
        return total
      })

      // Fila 4: supervisores por departamento
      // Fila 5: confirmaciones al año por departamento = Σ (total de confirmaciones del rol × empleados del rol)
      let subtotalColIndex = 0
      departmentSpans.forEach(({ locationId, span }) => {
        headerSup[2 + subtotalColIndex] = supervisorsByLocationMap.get(String(locationId)) || 0

        let subtotal = 0
        for (let i = subtotalColIndex; i < subtotalColIndex + span; i++) {
          const employees = employeesByRoleMap.get(`${columnMapping[i].locationId}|${columnMapping[i].roleId}`) || 0
          subtotal += columnTotals[i] * employees
        }
        headerSub[2 + subtotalColIndex] = subtotal
        subtotalColIndex += span
      })

      // Fila 7: empleados por rol
      columnMapping.forEach((col, colIndex) => {
        headerEmp[2 + colIndex] = employeesByRoleMap.get(`${col.locationId}|${col.roleId}`) || 0
      })

      // Agregar fila de totales
      const totalLabel = i18n.t('total_process_confirmations_per_year')
      const totalRow = [totalLabel, '']

      columnTotals.forEach(total => totalRow.push(total || ''))

      dataRows.push(totalRow)

      // 4. Combinar headers y datos
      const finalData = [...headers, ...dataRows]

      // 5. Crear workbook y worksheet
      const ws = XLSX.utils.aoa_to_sheet(finalData)

      // Agregar estilos y merge
      const range = XLSX.utils.decode_range(ws['!ref'])

      // Merge cells para headers
      const merges = []

      // Merge de "Procesos" en columna 0 desde fila 7 (A8) hasta la última fila de procesos (sin incluir la fila de totales)
      const firstDataRow = 7 // Fila 8 en Excel (índice 7)
      const lastProcessRow = 6 + sortedProcesses.length // Última fila de procesos (sin totales)
      if (sortedProcesses.length > 0) {
        merges.push({
          s: { r: firstDataRow, c: 0 },
          e: { r: lastProcessRow, c: 0 }
        })
      }

      // Merge physical locations en header1 (ahora en fila 1)
      let startCol = 2
      const physicalSpans = [] // Para marcar con borde grueso la sección de datos de cada localidad
      sortedPhysicalLocations.forEach(([physicalLocationId, locationsMap]) => {
        let colSpan = 0
        locationsMap.forEach((rolesSet) => {
          colSpan += rolesSet.size
        })

        physicalSpans.push({ startCol, colSpan })

        if (colSpan > 1) {
          merges.push({
            s: { r: 1, c: startCol },
            e: { r: 1, c: startCol + colSpan - 1 }
          })
        }
        startCol += colSpan
      })

      // Merge locations en header2 (ahora en fila 2)
      startCol = 2
      sortedPhysicalLocations.forEach(([physicalLocationId, locationsMap]) => {
        const sortedLocations = Array.from(locationsMap.entries()).sort((a, b) => {
          const nameA = locationNames.get(a[0])
          const nameB = locationNames.get(b[0])
          return nameA.localeCompare(nameB)
        })

        sortedLocations.forEach(([locationId, rolesSet]) => {
          const colSpan = rolesSet.size
          if (colSpan > 1) {
            merges.push({
              s: { r: 2, c: startCol },
              e: { r: 2, c: startCol + colSpan - 1 }
            })
          }
          startCol += colSpan
        })
      })

      // Merge para "Sin localidad asignada" y "Sin departamento asignado"
      if (noLocationRoles.size > 0) {
        const noLocationColSpan = noLocationRoles.size
        physicalSpans.push({ startCol, colSpan: noLocationColSpan })

        // Merge "Sin localidad asignada" en header1 (fila 1)
        if (noLocationColSpan > 1) {
          merges.push({
            s: { r: 1, c: startCol },
            e: { r: 1, c: startCol + noLocationColSpan - 1 }
          })
        }

        // Merge "Sin departamento asignado" en header2 (fila 2)
        if (noLocationColSpan > 1) {
          merges.push({
            s: { r: 2, c: startCol },
            e: { r: 2, c: startCol + noLocationColSpan - 1 }
          })
        }
      }

      // Merge de supervisores (fila 3) y confirmaciones por departamento (fila 4), mismo ancho que el departamento
      startCol = 2
      departmentSpans.forEach(({ span }) => {
        if (span > 1) {
          merges.push({
            s: { r: 3, c: startCol },
            e: { r: 3, c: startCol + span - 1 }
          })
          merges.push({
            s: { r: 4, c: startCol },
            e: { r: 4, c: startCol + span - 1 }
          })
        }
        startCol += span
      })

      // Merge de la fila de totales: columnas A y B
      const totalRowIndex = lastProcessRow + 1
      merges.push({
        s: { r: totalRowIndex, c: 0 },
        e: { r: totalRowIndex, c: 1 }
      })

      ws['!merges'] = merges

      // 6. Aplicar estilos
      const centerStyle = { alignment: { horizontal: 'center', vertical: 'center' } }
      const boldCenterStyle = { font: { bold: true }, alignment: { horizontal: 'center', vertical: 'center' } }

      // Centrar los valores numéricos de las filas de procesos
      const lastDataRowIndex = finalData.length - 1
      for (let r = firstDataRow; r <= lastDataRowIndex; r++) {
        for (let c = 2; c <= range.e.c; c++) {
          const cellRef = XLSX.utils.encode_cell({ r, c })
          const cell = ws[cellRef]
          if (cell && cell.t === 'n') {
            cell.s = centerStyle
          }
        }
      }

      // Aplicar estilo bold/center a las filas de supervisores (3), confirmaciones (4), empleados (6) y totales
      // La fila de totales es la última fila de la tabla
      const totalDataRowIndex = lastDataRowIndex
      for (const r of [3, 4, 6, totalDataRowIndex]) {
        for (let c = 2; c <= range.e.c; c++) {
          const cellRef = XLSX.utils.encode_cell({ r, c })
          const cell = ws[cellRef]
          if (cell) {
            cell.s = boldCenterStyle
          }
        }
      }

      // "Procesos" en bold y centrado en toda su celda combinada
      if (sortedProcesses.length > 0) {
        for (let r = firstDataRow; r <= lastProcessRow; r++) {
          const cell = ws[XLSX.utils.encode_cell({ r, c: 0 })]
          if (cell) {
            cell.s = boldCenterStyle
          }
        }
      }

      // "Total de Confirmaciones de Proceso / año" en bold y centrado en su celda combinada A:B
      const totalLabelCell = ws[XLSX.utils.encode_cell({ r: totalDataRowIndex, c: 0 })]
      if (totalLabelCell) {
        totalLabelCell.s = boldCenterStyle
      }

      // Centrar localidades (fila 1) y departamentos (fila 2)
      for (const r of [1, 2]) {
        for (let c = 2; c <= range.e.c; c++) {
          const cell = ws[XLSX.utils.encode_cell({ r, c })]
          if (cell && cell.v) {
            cell.s = centerStyle
          }
        }
      }

      // Bordes en todas las celdas de la tabla (crear las que faltan dentro de celdas combinadas)
      // Excluir la fila 1 (vacía) y las celdas A2:B2 (encima del título "Departamento") que no forman parte de la tabla
      const thinBorder = { style: 'thin' }
      for (let r = 0; r <= lastDataRowIndex; r++) {
        for (let c = 0; c <= range.e.c; c++) {
          const isOutsideLabelZone = r === 0 || (r <= 6 && c <= 1 && !(c === 1 && r >= 2))
          if (isOutsideLabelZone) continue

          const cellRef = XLSX.utils.encode_cell({ r, c })
          if (!ws[cellRef]) {
            ws[cellRef] = { t: 's', v: '' }
          }
          const existing = ws[cellRef].s || {}
          ws[cellRef].s = {
            ...existing,
            border: { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder },
          }
        }
      }

      // Bordes negros gruesos alrededor de toda la sección de datos de cada localidad
      // (desde el nombre de la localidad hasta la fila de totales, contorneando el bloque completo).
      // La fila de la localidad además lleva borde grueso inferior en sus celdas
      const thickBorder = { style: 'medium' }
      const setThickEdges = (r, c, edges) => {
        const cellRef = XLSX.utils.encode_cell({ r, c })
        if (!ws[cellRef]) {
          ws[cellRef] = { t: 's', v: '' }
        }
        const existing = ws[cellRef].s || {}
        const prev = existing.border || {}
        ws[cellRef].s = {
          ...existing,
          border: {
            top: edges.top || prev.top || thinBorder,
            bottom: edges.bottom || prev.bottom || thinBorder,
            left: edges.left || prev.left || thinBorder,
            right: edges.right || prev.right || thinBorder,
          },
        }
      }
      physicalSpans.forEach(({ startCol, colSpan }) => {
        const endCol = startCol + colSpan - 1
        for (let r = 1; r <= lastDataRowIndex; r++) {
          setThickEdges(r, startCol, { left: thickBorder })
          setThickEdges(r, endCol, { right: thickBorder })
        }
        for (let c = startCol; c <= endCol; c++) {
          setThickEdges(1, c, { top: thickBorder, bottom: thickBorder })
          setThickEdges(lastDataRowIndex, c, { bottom: thickBorder })
        }
      })

      // Color de fondo gris para la fila de localidades (C2 hacia la derecha)
      // y para las etiquetas de datos (A8:B8 hacia abajo)
      const labelFill = { fgColor: { rgb: 'F2F2F2' } }
      for (let c = 2; c <= range.e.c; c++) {
        const cellRef = XLSX.utils.encode_cell({ r: 1, c })
        if (!ws[cellRef]) {
          ws[cellRef] = { t: 's', v: '' }
        }
        const existing = ws[cellRef].s || {}
        ws[cellRef].s = { ...existing, fill: labelFill }
      }
      for (let r = firstDataRow; r <= lastDataRowIndex; r++) {
        for (let c = 0; c <= 1; c++) {
          const cellRef = XLSX.utils.encode_cell({ r, c })
          if (!ws[cellRef]) {
            ws[cellRef] = { t: 's', v: '' }
          }
          const existing = ws[cellRef].s || {}
          ws[cellRef].s = { ...existing, fill: labelFill }
        }
      }

      // Colores de fondo por fila (Verde oliva, Énfasis 3). La fila de departamentos queda sin relleno
      const rowFills = [
        { r: 3, rgb: 'EBF1DE' }, // Supervisores por Departamento: Claro 80%
        { r: 4, rgb: 'D8E4BC' }, // Confirmaciones al año por Departamento: Claro 60%
        { r: 5, rgb: 'C3D69B' }, // Roles: Claro 40%
        { r: 6, rgb: '76923C' }, // Empleados por Rol: Oscuro 25%
      ]
      rowFills.forEach(({ r, rgb }) => {
        const fill = { fgColor: { rgb } }
        for (let c = 1; c <= range.e.c; c++) {
          const cellRef = XLSX.utils.encode_cell({ r, c })
          if (!ws[cellRef]) {
            ws[cellRef] = { t: 's', v: '' }
          }
          const existing = ws[cellRef].s || {}
          ws[cellRef].s = { ...existing, fill }
        }
      })

      // Color de fondo para las celdas con valores numéricos (filas de procesos). Las celdas sin número quedan sin color
      const numberFill = { fgColor: { rgb: 'DDEBF7' } }
      for (let r = firstDataRow; r <= lastProcessRow; r++) {
        for (let c = 2; c <= range.e.c; c++) {
          const cell = ws[XLSX.utils.encode_cell({ r, c })]
          if (cell && cell.t === 'n') {
            const existing = cell.s || {}
            cell.s = { ...existing, fill: numberFill }
          }
        }
      }

      // Color de fondo para toda la fila de totales
      const totalFill = { fgColor: { rgb: 'D9D9D9' } }
      for (let c = 0; c <= range.e.c; c++) {
        const cellRef = XLSX.utils.encode_cell({ r: totalDataRowIndex, c })
        if (!ws[cellRef]) {
          ws[cellRef] = { t: 's', v: '' }
        }
        const existing = ws[cellRef].s || {}
        ws[cellRef].s = { ...existing, fill: totalFill }
      }

      // Ajustar ancho de columnas
      const colWidths = [{ wch: 10 }, { wch: 40 }] // "Procesos"/"Processes" y títulos de la columna B
      for (let i = 0; i < columnMapping.length; i++) {
        colWidths.push({ wch: 10 })
      }
      ws['!cols'] = colWidths

      // 6. Crear workbook y exportar
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Confirmation Planning')

      const fileName = `confirmation_planning_${new Date().toISOString().slice(0, 10)}.xlsx`

      // xlsx-js-style no soporta paneles inmovilizados: se generan los bytes y se inyecta el <pane> en el XML de la hoja
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      const zip = await JSZip.loadAsync(wbout)
      const sheetPath = Object.keys(zip.files).find(name => /^xl\/worksheets\/sheet\d+\.xml$/.test(name))
      let sheetXml = await zip.file(sheetPath).async('string')

      // Congelar en C8: columnas A-B y filas 1-7 (hasta "Empleados por Rol" inclusive) quedan fijas al scrollear
      const paneXml = '<pane ySplit="7" xSplit="2" topLeftCell="C8" activePane="bottomRight" state="frozen"/><selection pane="bottomRight" activeCell="C8" sqref="C8"/>'
      if (/<sheetView[^>]*\/>/.test(sheetXml)) {
        sheetXml = sheetXml.replace(/<sheetView([^>]*)\/>/, `<sheetView$1>${paneXml}</sheetView>`)
      } else {
        sheetXml = sheetXml.replace(/<sheetView([^>]*)>/, `<sheetView$1>${paneXml}`)
      }

      zip.file(sheetPath, sheetXml)

      const blob = await zip.generateAsync({ type: 'blob' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      showSuccessMessage(i18n.t('message.excelDownloaded'))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.excelDownloadError'))
    } finally {
      isExporting.value = false
    }
  }

  watch([currentPage, perPage], () => {
    fetchConfirmationPlannings()
  })

  watch([searchQuery, sortBy, isSortDirDesc], () => {
    currentPage.value = 1
    fetchConfirmationPlannings()
  })

  return {
    fetchConfirmationPlannings,
    tableColumns,
    perPage,
    currentPage,
    perPageOptions,
    totalConfirmationPlannings,
    showingMessage,
    searchQuery,
    sortBy,
    isSortDirDesc,
    confirmationPlannings,
    deleteConfirmationPlanning,
    isLoading,
    processFilter,
    roleFilter,
    updateFilter,
    exportToExcel,
    isExporting,
  }
}
